import { 
  Sun, 
  Cloud, 
  CloudSun, 
  CloudRain, 
  CloudSnow, 
  CloudLightning, 
  CloudFog,
  type LucideIcon
} from "lucide-react";

interface TicketWeatherBadgeProps {
  weatherConditions?: string | null;
}

/**
 * Maps weather condition text from WeatherAPI to appropriate Lucide icon.
 * WeatherAPI conditions include: Sunny, Clear, Partly cloudy, Cloudy, 
 * Overcast, Mist, Fog, Rain, Light rain, Heavy rain, Snow, Thunderstorm, etc.
 */
function getWeatherIcon(conditions: string | null | undefined): LucideIcon | null {
  if (!conditions) return null;
  
  const lower = conditions.toLowerCase();
  
  // Thunderstorm/storm conditions
  if (lower.includes('thunder') || lower.includes('storm')) {
    return CloudLightning;
  }
  
  // Snow conditions
  if (lower.includes('snow') || lower.includes('sleet') || lower.includes('blizzard') || lower.includes('ice')) {
    return CloudSnow;
  }
  
  // Rain conditions
  if (lower.includes('rain') || lower.includes('drizzle') || lower.includes('shower')) {
    return CloudRain;
  }
  
  // Fog/mist conditions
  if (lower.includes('fog') || lower.includes('mist') || lower.includes('haze')) {
    return CloudFog;
  }
  
  // Overcast/cloudy conditions
  if (lower.includes('overcast') || lower.includes('cloudy')) {
    return Cloud;
  }
  
  // Partly cloudy
  if (lower.includes('partly')) {
    return CloudSun;
  }
  
  // Sunny/clear conditions
  if (lower.includes('sunny') || lower.includes('clear')) {
    return Sun;
  }
  
  // Default fallback
  return Cloud;
}

/**
 * Simple weather icon display based on ticket's captured weather conditions.
 * No interaction, no modal - just a static icon reflecting the weather.
 */
export function TicketWeatherBadge({ weatherConditions }: TicketWeatherBadgeProps) {
  const Icon = getWeatherIcon(weatherConditions);
  
  // Don't render anything if no weather data
  if (!Icon) {
    return null;
  }
  
  return (
    <Icon className="h-4 w-4 text-slate-500" />
  );
}
